import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { enqueueToaster } from "@deere/isg.component-library";
import type { MachineSlice, PredicateContext, PredicateVerdict } from "./autonomyPredicates";

export const AUTONOMY_COMMAND_TIMEOUT_MS = 30_000;

export type AutonomyCommandType = "start" | "pause" | "resume" | "quit" | "clear-tasks";

export type CommandOutcome =
  | "success"
  | "failure-unexpected"
  | "failure-geometry"
  | "failure-timeout"
  | "cancelled";

export type MachinePath = {
  org: string;
  jobsite: string;
  session: string;
  machineType: string;
  machineModel: string;
  machineName: string;
};

export type PendingCommand = {
  id: string;
  commandType: AutonomyCommandType;
  machineDisplayName: string;
  path: MachinePath;
  predicate: (slice: MachineSlice, ctx: PredicateContext) => PredicateVerdict;
  onResolve: (outcome: CommandOutcome) => void;
  /** Overrides the default toasts, for steps inside a larger multi-phase flow. */
  toasts?: {
    /** Skip the success toast so only the final step of a flow announces itself. */
    silentSuccess?: boolean;
    /** Replaces the body of every failure toast for this command. */
    failureMessage?: string;
  };
};

export type PendingCommandsContextValue = {
  register: (cmd: PendingCommand) => void;
  unregister: (id: string) => void;
  hasPending: (machineName?: string) => boolean;
};

type StoredCommand = PendingCommand & {
  timeoutId: ReturnType<typeof setTimeout>;
  registeredAt: number;
};

export const PendingCommandsContext = createContext<PendingCommandsContextValue | null>(null);

function getSliceByPath(mqttData: unknown, path: MachinePath): MachineSlice | undefined {
  return (mqttData as any)?.[path.org]?.[path.jobsite]?.[path.session]?.[path.machineType]?.[
    path.machineModel
  ]?.[path.machineName];
}

function commandLabel(type: AutonomyCommandType): string {
  return {
    start: "Start Autonomy",
    pause: "Pause",
    resume: "Resume",
    quit: "Quit Mission",
    "clear-tasks": "Clear Tasks",
  }[type];
}

export function PendingCommandsProvider({
  children,
  mqttData,
  orgId,
  onNavigateToMachine,
}: {
  children: ReactNode;
  mqttData: unknown;
  orgId: string;
  onNavigateToMachine?: (path: MachinePath) => void;
}) {
  const pendingRef = useRef<Map<string, StoredCommand>>(new Map());
  const prevOrgIdRef = useRef<string>(orgId);
  const [pendingMachines, setPendingMachines] = useState<Set<string>>(new Set());

  const syncPendingMachines = useCallback(() => {
    const next = new Set<string>();
    pendingRef.current.forEach((c) => next.add(c.path.machineName));
    setPendingMachines(next);
  }, []);

  const resolveAndCleanup = useCallback(
    (cmd: StoredCommand, outcome: CommandOutcome) => {
      clearTimeout(cmd.timeoutId);
      pendingRef.current.delete(cmd.id);
      syncPendingMachines();
      cmd.onResolve(outcome);

      const label = commandLabel(cmd.commandType);

      const renderGoToMachineButton = () =>
        onNavigateToMachine && cmd.path.org === orgId ? (
          <button
            onClick={() => onNavigateToMachine(cmd.path)}
            style={{
              marginLeft: "12px",
              padding: "4px 12px",
              cursor: "pointer",
              background: "white",
              color: "#d32f2f",
              border: "1px solid #d32f2f",
              borderRadius: "4px",
              fontWeight: 600,
              fontSize: "13px",
            }}
          >
            Go to machine
          </button>
        ) : null;

      if (outcome === "success") {
        if (cmd.toasts?.silentSuccess) return;
        enqueueToaster(`${label} confirmed for ${cmd.machineDisplayName}`, "", {
          variant: "success",
        });
      } else if (outcome === "failure-unexpected") {
        enqueueToaster(
          <>
            {cmd.toasts?.failureMessage ??
              `${cmd.machineDisplayName}: unexpected machine behavior during ${label}.`}
            {renderGoToMachineButton()}
          </>,
          "Command Failed",
          { variant: "error", isPersistent: true, isCloseable: true },
        );
      } else if (outcome === "failure-geometry") {
        // Wording tracks GEOMETRY_FAULT_DEBUG_STRINGS in navigatorStateHelper.
        enqueueToaster(
          cmd.toasts?.failureMessage ??
            `${cmd.machineDisplayName} cannot run autonomy: the jobsite is missing a valid outer boundary. Check the jobsite geometry and try again.`,
          "Jobsite Geometry Error",
          { variant: "error", isPersistent: true, isCloseable: true },
        );
      } else if (outcome === "failure-timeout") {
        enqueueToaster(
          cmd.toasts?.failureMessage ??
            `${cmd.machineDisplayName} did not respond to ${label} command. Please check the machine and try again.`,
          "Command Timeout",
          { variant: "error", isPersistent: true, isCloseable: true },
        );
      }
    },
    [syncPendingMachines, onNavigateToMachine, orgId],
  );

  const register = useCallback(
    (cmd: PendingCommand) => {
      const timeoutId = setTimeout(() => {
        const stored = pendingRef.current.get(cmd.id);
        if (stored) resolveAndCleanup(stored, "failure-timeout");
      }, AUTONOMY_COMMAND_TIMEOUT_MS);

      pendingRef.current.set(cmd.id, { ...cmd, timeoutId, registeredAt: Date.now() });
      syncPendingMachines();
    },
    [resolveAndCleanup, syncPendingMachines],
  );

  const unregister = useCallback(
    (id: string) => {
      const stored = pendingRef.current.get(id);
      if (stored) {
        clearTimeout(stored.timeoutId);
        pendingRef.current.delete(id);
        syncPendingMachines();
      }
    },
    [syncPendingMachines],
  );

  const hasPending = useCallback(
    (machineName?: string) => {
      if (machineName === undefined) return pendingMachines.size > 0;
      return pendingMachines.has(machineName);
    },
    [pendingMachines],
  );

  useEffect(() => {
    if (pendingRef.current.size === 0) return;
    pendingRef.current.forEach((cmd) => {
      const slice = getSliceByPath(mqttData, cmd.path);
      if (!slice) return;
      const verdict = cmd.predicate(slice, {
        msSinceRegistered: Date.now() - cmd.registeredAt,
      });
      if (verdict !== "pending") resolveAndCleanup(cmd, verdict);
    });
  }, [mqttData, resolveAndCleanup]);

  useEffect(() => {
    if (prevOrgIdRef.current === orgId) return;
    prevOrgIdRef.current = orgId;

    const count = pendingRef.current.size;
    if (count === 0) return;

    // Resolve before clearing so callers can unwind loading/dialog state; the
    // aggregate warning below replaces the usual per-command toaster.
    pendingRef.current.forEach((cmd) => {
      clearTimeout(cmd.timeoutId);
      cmd.onResolve("cancelled");
    });
    pendingRef.current.clear();
    syncPendingMachines();

    enqueueToaster(
      `${count} pending command confirmation${count === 1 ? "" : "s"} ${
        count === 1 ? "was" : "were"
      } cancelled due to organization switch`,
      "",
      { variant: "warning" },
    );
  }, [orgId, syncPendingMachines]);

  const value = useMemo<PendingCommandsContextValue>(
    () => ({ register, unregister, hasPending }),
    [register, unregister, hasPending],
  );

  return (
    <PendingCommandsContext.Provider value={value}>{children}</PendingCommandsContext.Provider>
  );
}

export function usePendingCommands(): PendingCommandsContextValue {
  const ctx = useContext(PendingCommandsContext);
  if (!ctx) {
    throw new Error("usePendingCommands must be used within a PendingCommandsProvider");
  }
  return ctx;
}
